import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Send, Eye, Loader2, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function DigestPanel() {
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["digest-preview"],
    queryFn: () => api.get("/digest/preview").then((r) => r.data),
  });

  const send = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/digest/send", { subject: subject || undefined });
      toast.success(res.message || "Digest sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">Weekly Ecosystem Digest</h2>
          <p className="text-sm text-[#4A5D6B]">A rendered email of latest articles, upcoming events, and a featured testimonial.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border border-[#E5E0D8] bg-white p-5">
            <div className="flex items-center gap-2 text-[#144F75]"><Users size={18} /><span className="font-semibold">{data?.subscriber_count ?? 0} subscribers</span></div>
            <div className={`mt-4 flex items-start gap-2 text-sm rounded-lg p-3 ${data?.mailchimp_configured ? "bg-[#8FA89B]/15 text-[#144F75]" : "bg-[#D98E73]/15 text-[#8a4a30]"}`}>
              {data?.mailchimp_configured ? <CheckCircle2 size={18} className="shrink-0 mt-0.5" /> : <AlertTriangle size={18} className="shrink-0 mt-0.5" />}
              <span>{data?.mailchimp_configured ? "Mailchimp connected — sending is live." : "Mailchimp not configured. Preview works; add API keys in backend/.env to enable real sending."}</span>
            </div>
          </div>

          <div className="rounded-xl border border-[#E5E0D8] bg-white p-5 space-y-3">
            <label className="text-sm font-medium text-[#1A242B] block">Subject line</label>
            <input data-testid="digest-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ecosystem Digest — this week"
              className="w-full rounded-lg border border-[#E5E0D8] px-3.5 py-2.5 text-sm outline-none focus:border-[#144F75]" />
            <button data-testid="digest-refresh-btn" onClick={() => refetch()} className="eyw-btn-ghost !py-2.5 text-sm w-full"><Eye size={16} /> Refresh Preview</button>
            <button data-testid="digest-send-btn" onClick={send} disabled={sending} className="eyw-btn !py-2.5 text-sm w-full disabled:opacity-60">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send Digest
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-[#E5E0D8] bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#E5E0D8] text-xs text-[#4A5D6B] uppercase tracking-widest">Live Preview</div>
          {isLoading ? (
            <div className="p-10 text-center text-[#4A5D6B]">Loading preview…</div>
          ) : (
            <iframe data-testid="digest-preview-frame" title="Digest Preview" srcDoc={data?.html} className="w-full h-[640px] border-0" />
          )}
        </div>
      </div>
    </div>
  );
}
